import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { sowService } from '../../services/sowService';
import { SOW } from '../../models/sow';
import { 
  ArrowLeft, 
  FileText, 
  User, 
  MapPin, 
  Building, 
  Home, 
  Bed, 
  Bath, 
  DollarSign,
  Clock,
  CheckCircle,
  PlayCircle,
  AlertCircle,
  Loader2,
  Trash2,
  ChevronRight
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';
import { useAuth0 } from '@auth0/auth0-react';
import { sowConversationService } from '../../services/sowConversationService';
import { ImageGallery } from '../../components/ImageGallery';
import { ProjectEstimate } from '../../components/ProjectEstimate';

interface SOWOverviewPageProps {}

export const SOWOverviewPage: React.FC<SOWOverviewPageProps> = () => {
  const { sowId } = useParams<{ sowId: string }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  const { getAccessTokenSilently } = useAuth0();
  
  const [sow, setSOW] = useState<SOW | null>(null);
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [filteredQuestionCounts, setFilteredQuestionCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllItems, setShowAllItems] = useState(false);
  const [preventAutoRedirect, setPreventAutoRedirect] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [projectEstimate, setProjectEstimate] = useState<any>(null);

  useEffect(() => {
    if (sowId && tokenSet && !tokenLoading) {
      // Check if user is coming back from a questionnaire page
      const fromQuestionnaire = document.referrer.includes('/questionnaire/');
      if (fromQuestionnaire) {
        setPreventAutoRedirect(true);
      }
      loadSOW(sowId);
    }
  }, [sowId, tokenSet, tokenLoading]);

  const loadSOW = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const sowData = await sowService.getSOW(id);
      setSOW(sowData);
      
      // Check if SOW is incomplete and should redirect to wizard
      const isIncomplete = !sowData.address || !sowData.propertyData || !sowData.scopeDefinition;
      const isDraftStatus = sowData.status === 'draft' || sowData.status === 'scope-gathering' || !sowData.status;
      
      if (isIncomplete || isDraftStatus) {
        // SOW is not ready for overview, redirect to wizard
        navigate(`/sow/${id}/wizard`);
        return;
      }

      // Check for existing estimate if SOW has projectPlanId or is estimate_ready
      if (sowData.projectPlanId || sowData.status === 'estimate_ready') {
        try {
          console.log('Loading existing project estimate...');
          const estimateResult = await sowService.generateProjectEstimate(id);
          setProjectEstimate(estimateResult);
        } catch (error) {
          console.error('Failed to load existing estimate:', error);
          // Continue loading the page even if estimate loading fails
        }
      }

      // Load questionnaires if SOW is in estimating status (NEW ARCHITECTURE)
      if (sowData.status === 'estimating') {
        try {
          const questionnairesData = await sowConversationService.getQuestionnaires(id);
          setQuestionnaires(questionnairesData.questionnaires);
          // Update work items with latest data from questionnaire evaluation
          setSOW(prev => prev ? { ...prev, workItems: questionnairesData.workItems } : null);
        } catch (qError) {
          console.log('No questionnaires found, using legacy work item refinement');
          setQuestionnaires([]);
        }
      }
    } catch (err) {
      console.error('Error loading SOW:', err);
      setError('Failed to load SOW. It may not exist or you may not have access.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'complete':
        return { 
          icon: CheckCircle, 
          variant: 'default' as const, 
          color: 'text-green-600',
          description: 'Project completed - all work items finished'
        };
      case 'estimating':
        return { 
          icon: Clock, 
          variant: 'secondary' as const, 
          color: 'text-blue-600',
          description: 'Managing work items and tracking progress'
        };
      case 'initialQuestionnaire':
        return { 
          icon: FileText, 
          variant: 'outline' as const, 
          color: 'text-blue-600',
          description: 'Ready for questionnaire to generate work items'
        };
      case 'scope-gathering':
        return { 
          icon: PlayCircle, 
          variant: 'outline' as const, 
          color: 'text-orange-600',
          description: 'Setting up project scope and details'
        };
      default:
        return { 
          icon: AlertCircle, 
          variant: 'outline' as const, 
          color: 'text-gray-600',
          description: 'Project status unknown'
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStartQuestionnaire = () => {
    navigate(`/sow/${sowId}/questionnaire`);
  };

  const handleContinue = () => {
    if (sow?.status === 'initialQuestionnaire' && !sow.questionnaireCompleted) {
      navigate(`/sow/${sowId}/questionnaire`);
    } else if (sow?.status === 'draft' || sow?.status === 'scope-gathering') {
      navigate(`/sow/${sowId}/wizard`);
    } else if (sow?.status === 'estimating') {
      // NEW ARCHITECTURE: Navigate to first pending questionnaire or stay on overview
      const pendingQuestionnaires = questionnaires.filter((q: any) => q.status === 'pending');
      if (pendingQuestionnaires.length > 0) {
        navigate(`/sow/${sowId}/questionnaire/${pendingQuestionnaires[0].id}`);
      } else {
        // All questionnaires complete, scroll to work items section
        const workItemsSection = document.querySelector('[data-section="work-items"]');
        if (workItemsSection) {
          workItemsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleViewResults = () => {
    setShowAllItems(!showAllItems);
  };

  const handleStartRefinement = async (workItemId: string) => {
    if (!sowId) return;
    
    try {
      // Start refinement conversation
      const response = await sowConversationService.startWorkItemRefinement(sowId, workItemId);
      
      // Navigate to refinement conversation (we'll create this route)
      navigate(`/sow/${sowId}/workitem/${workItemId}/refinement/${response.conversation.id}`);
    } catch (error) {
      console.error('Error starting work item refinement:', error);
    }
  };

  const handleContinueRefinement = (conversationId: string, workItemId: string) => {
    navigate(`/sow/${sowId}/workitem/${workItemId}/refinement/${conversationId}`);
  };

  const handleRemoveWorkItem = async (workItemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to remove this work item?')) {
      return;
    }

    try {
      // TODO: Implement remove work item API call
      // await sowService.removeWorkItem(sowId, workItemId);
      
      // For now, just reload the SOW data
      if (sowId) {
        loadSOW(sowId);
      }
    } catch (error) {
      console.error('Error removing work item:', error);
    }
  };

  const handleGenerateEstimate = async () => {
    if (!sowId) return;
    
    try {
      setEstimateLoading(true);
      setError(null);
      
      const estimateResult = await sowService.generateProjectEstimate(sowId);
      setProjectEstimate(estimateResult);
      
    } catch (error) {
      console.error('Error generating estimate:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate estimate');
    } finally {
      setEstimateLoading(false);
    }
  };

  const handleViewEstimateDetails = () => {
    // TODO: Navigate to detailed estimate view
    console.log('View estimate details:', projectEstimate);
  };

  const handleDownloadEstimate = async () => {
    if (!projectEstimate || !sow || !sowId) {
      console.error('Missing project estimate, SOW data, or SOW ID for PDF generation');
      return;
    }

    try {
      console.log('📄 Downloading PDF estimate from backend...');
      
      // Get the access token
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: 'https://fxer.ai/api',
          scope: 'read:all',
        },
      });
      
      // Call the backend PDF generation endpoint  
      const response = await fetch(`http://localhost:3000/statementofwork/${sowId}/estimate/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName = contentDisposition?.match(/filename="([^"]+)"/)?.[1] || 'Project_Estimate.pdf';
      a.download = fileName;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ PDF downloaded successfully');
      
    } catch (error) {
      console.error('❌ Failed to download PDF:', error);
      // Could add a toast notification here
    }
  };

  // Sort work items: pending first, in_progress second, completed (auto-refined) last
  const sortWorkItems = (items: any[]) => {
    return items
      .filter(item => item.status !== 'REMOVED')
      .sort((a, b) => {
        // Items without refinementStatus (inspection items) go to the end
        if (!a.refinementStatus && !b.refinementStatus) return 0;
        if (!a.refinementStatus) return 1;
        if (!b.refinementStatus) return -1;
        
        const statusOrder: Record<string, number> = { 'pending': 1, 'in_progress': 2, 'completed': 3 };
        return (statusOrder[a.refinementStatus] || 999) - (statusOrder[b.refinementStatus] || 999);
      });
  };

  // Get work item counts for different statuses
  const getWorkItemCounts = () => {
    const questionnaire = sow?.workItems.filter(item => item.refinementStatus) || [];
    const inspection = sow?.workItems.filter(item => !item.refinementStatus && item.status !== 'REMOVED') || [];
    
    return {
      needsDetails: questionnaire.filter(item => item.refinementStatus === 'pending').length,
      needsRefinement: inspection.length, // Inspection items that need manual refinement
      inProgress: questionnaire.filter(item => item.refinementStatus === 'in_progress').length,
      completed: questionnaire.filter(item => item.refinementStatus === 'completed').length,
      total: questionnaire.length + inspection.length
    };
  };


  if (loading || tokenLoading || !tokenSet) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <div className="text-lg">Loading SOW...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sow) {
    return (
      <div className="container mx-auto p-6">
        <Card className="text-center py-12">
          <CardContent className="pt-6">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">SOW Not Found</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/sow')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to SOWs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(sow.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/sow')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to SOWs
        </Button>
        
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Project</h1>
            <div className="flex items-center gap-2 text-gray-600">
              <MapPin className="h-4 w-4" />
              <span>{sow.address}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge variant={statusInfo.variant} className="flex items-center gap-2">
              <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
              {sow.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Project Status - Top Level */}
      <div className="mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                <div>
                  <div className="font-medium">Project Status: {sow.status}</div>
                  <div className="text-sm text-gray-600">{statusInfo.description}</div>
                </div>
              </div>
              {((sow.status === 'initialQuestionnaire' && !sow.questionnaireCompleted) || 
                sow.status === 'draft' || 
                sow.status === 'scope-gathering' ||
                sow.status === 'estimating') && (
                <Button onClick={handleContinue}>
                  Continue
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content - Work Items Focus */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Estimate Display - Priority Display */}
          {projectEstimate && (
            <ProjectEstimate 
              projectEstimate={projectEstimate}
              onViewDetails={handleViewEstimateDetails}
              onDownloadEstimate={handleDownloadEstimate}
            />
          )}

          {/* Work Items - Primary Focus */}
          <Card data-section="work-items">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Work Items
                  {sow.workItems.length > 0 && (
                    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {getWorkItemCounts().completed}/{getWorkItemCounts().total} ready
                    </div>
                  )}
                </div>
                {sow.workItems.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleViewResults}>
                    {showAllItems ? 'Show Less' : `View All (${getWorkItemCounts().total})`}
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Refinement Progress and Instructions */}
              {sow.status === 'estimating' && sow.workItems.length > 0 && questionnaires.length > 0 && (
                <div className="mb-4">
                  {(() => {
                    const pendingQuestionnaires = questionnaires.filter((q: any) => q.status === 'pending');
                    const inProgressQuestionnaires = questionnaires.filter((q: any) => q.status === 'in_progress');
                    const completedQuestionnaires = questionnaires.filter((q: any) => q.status === 'completed');
                    const totalQuestions = questionnaires.reduce((sum: number, q: any) => sum + (q.questions?.length || 0), 0);
                    
                    // All questionnaires complete - show estimate generation
                    if (pendingQuestionnaires.length === 0 && inProgressQuestionnaires.length === 0) {
                      return (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-3 mb-3">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <div>
                              <h4 className="font-medium text-green-900">We have the data we need</h4>
                              <p className="text-sm text-green-700">All refinement questionnaires completed</p>
                            </div>
                          </div>
                          <Button 
                            className="w-full bg-green-600 hover:bg-green-700"
                            size="sm"
                            onClick={handleGenerateEstimate}
                            disabled={estimateLoading}
                          >
                            {estimateLoading ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <DollarSign className="h-4 w-4 mr-2" />
                            )}
                            {estimateLoading ? 'Generating...' : 'Generate Estimate'}
                          </Button>
                        </div>
                      );
                    }
                    
                    // Still have questionnaires to complete - show progress
                    return (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-3">
                          <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-medium text-blue-900 mb-2">Refinement Progress</h4>
                            <p className="text-sm text-blue-700 mb-3">
                              Answer questionnaires to provide more details for work items that need refinement.
                            </p>
                            
                            {/* Progress Stats */}
                            <div className="grid grid-cols-3 gap-4 text-center mb-3">
                              <div className="bg-white/60 rounded-lg p-2">
                                <div className="text-lg font-bold text-orange-600">
                                  {pendingQuestionnaires.length}
                                </div>
                                <div className="text-xs text-orange-700">To Start</div>
                              </div>
                              <div className="bg-white/60 rounded-lg p-2">
                                <div className="text-lg font-bold text-blue-600">
                                  {inProgressQuestionnaires.length}
                                </div>
                                <div className="text-xs text-blue-700">In Progress</div>
                              </div>
                              <div className="bg-white/60 rounded-lg p-2">
                                <div className="text-lg font-bold text-green-600">
                                  {completedQuestionnaires.length}
                                </div>
                                <div className="text-xs text-green-700">Complete</div>
                              </div>
                            </div>

                            <Button 
                              onClick={() => {
                                const nextQuestionnaire = inProgressQuestionnaires[0] || pendingQuestionnaires[0];
                                navigate(`/sow/${sowId}/questionnaire/${nextQuestionnaire.id}`);
                              }}
                              className="w-full"
                              size="sm"
                            >
                              <PlayCircle className="h-4 w-4 mr-2" />
                              {inProgressQuestionnaires.length > 0 ? 'Continue' : 'Start'} Refinement
                              {totalQuestions > 0 && (
                                <span className="ml-2 text-xs opacity-90">
                                  (~{totalQuestions} questions)
                                </span>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {sow.workItems.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Work Items Yet</h3>
                  <p className="text-gray-600 mb-4">
                    {!sow.questionnaireCompleted 
                      ? "Complete the questionnaire to generate your work items"
                      : sow.questionnaireCompleted && sow.workItems.length === 0
                        ? "Your work items are being generated..."
                        : "Work items will appear here once available"
                    }
                  </p>
                  {!sow.questionnaireCompleted && (
                    <Button onClick={handleStartQuestionnaire}>
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Start Questionnaire
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {sow.workItems.some(item => item.status === 'COMPLETE') && (
                    <div className="flex justify-end items-center">
                      <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-700">
                        Clear Completed
                      </Button>
                    </div>
                  )}
                  
                  {/* Work items list */}
                  <div className="divide-y divide-gray-100">
                    {(() => {
                      // When questionnaires are complete, always collapse to fewer items unless explicitly expanded
                      const allQuestionnairesComplete = questionnaires.length > 0 && 
                        questionnaires.filter((q: any) => q.status === 'pending' || q.status === 'in_progress').length === 0;
                      
                      // If questionnaires complete: show 0 items max unless showAllItems is true
                      // If questionnaires not complete: show 3 items unless showAllItems is true
                      const defaultItemCount = allQuestionnairesComplete ? 0 : 3;
                      const itemsToShow = showAllItems ? undefined : defaultItemCount;
                      
                      return sortWorkItems(sow.workItems).slice(0, itemsToShow);
                    })().map((item) => (
                      <div 
                        key={item.id} 
                        className="py-4 px-3 -mx-3 rounded-lg"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <h4 className="font-medium text-gray-900 truncate">{item.description}</h4>
                              
                              {/* Status indicator - explains WHY questionnaires are needed */}
                              {item.refinementStatus === 'pending' && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 whitespace-nowrap">
                                  Needs details
                                </span>
                              )}
                              {item.refinementStatus === 'in_progress' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <Clock className="h-3 w-3 mr-1" />
                                  In progress
                                </span>
                              )}
                              {item.refinementStatus === 'completed' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Complete
                                </span>
                              )}
                            </div>
                            
                            <div className="text-sm text-gray-600">
                              {item.costEstimate ? (
                                `$${item.costEstimate.toLocaleString()}`
                              ) : item.estimatedCostLow && item.estimatedCostHigh ? (
                                `$${item.estimatedCostLow.toLocaleString()} - $${item.estimatedCostHigh.toLocaleString()}`
                              ) : (
                                'Cost pending details'
                              )}
                              {item.category && (
                                <span className="text-gray-400 ml-2">• {item.category}</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {/* Remove button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-2 h-8 w-8 text-red-500 hover:bg-red-50"
                              onClick={(e) => handleRemoveWorkItem(item.id, e)}
                              title="Remove work item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Continue/Show more section */}
                  {!showAllItems && (
                    <div className="text-center pt-4 border-t border-gray-100">
                      {(() => {
                        const allQuestionnairesComplete = questionnaires.length > 0 && 
                          questionnaires.filter((q: any) => q.status === 'pending' || q.status === 'in_progress').length === 0;
                        const defaultItemCount = allQuestionnairesComplete ? 0 : 3;
                        return getWorkItemCounts().total > defaultItemCount;
                      })() ? (
                        <div className="space-y-2">
                          {(() => {
                            const allQuestionnairesComplete = questionnaires.length > 0 && 
                              questionnaires.filter((q: any) => q.status === 'pending' || q.status === 'in_progress').length === 0;
                            const defaultItemCount = allQuestionnairesComplete ? 0 : 3;
                            const remainingItems = getWorkItemCounts().total - defaultItemCount;
                            return (
                              <p className="text-sm text-gray-500">
                                {remainingItems} more item{remainingItems !== 1 ? 's' : ''}
                              </p>
                            );
                          })()}
                          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowAllItems(true)}>
                            Review All Items
                          </Button>
                        </div>
                      ) : (
                        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleContinue}>
                          Continue
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>


          {/* Questionnaire Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Initial Scope
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  
                  {/* Action Buttons Based on Status */}
                  {sow.status === 'initialQuestionnaire' && !sow.questionnaireCompleted && (
                    <div className="space-y-3">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-3">
                          <PlayCircle className="h-6 w-6 text-blue-600" />
                          <div>
                            <h4 className="font-medium text-blue-900">Ready for Questionnaire</h4>
                            <p className="text-sm text-blue-700">
                              Answer questions about your {sow.scopeDefinition?.projectType.toLowerCase()} project to generate work items.
                            </p>
                          </div>
                        </div>
                      </div>
                      <Button onClick={handleStartQuestionnaire} className="w-full">
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Start Project Questionnaire
                      </Button>
                    </div>
                  )}
                  
                  {/* Questionnaire Complete Status */}
                  {(sow.status === 'initialQuestionnaire' || sow.status === 'estimating') && sow.questionnaireCompleted && (
                    <div className="space-y-3">
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                          <div>
                            <h4 className="font-medium text-green-900">Questionnaire Complete</h4>
                            <p className="text-sm text-green-700">
                              Your project questionnaire has been completed. Work items are being generated.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  
                  {sow.status === 'complete' && sow.workItems.length > 0 && (
                    <div className="space-y-3">
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                          <div>
                            <h4 className="font-medium text-green-900">Project Complete</h4>
                            <p className="text-sm text-green-700">
                              {sow.workItems.length} work items have been generated for your project.
                            </p>
                          </div>
                        </div>
                      </div>
                      <Button onClick={handleViewResults} variant="outline" className="w-full">
                        <FileText className="h-4 w-4 mr-2" />
                        View Detailed Results
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Project Details */}
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Created</label>
                    <p className="text-sm">{formatDate(sow.createdAt.toString())}</p>
                  </div>
                  
                  {sow.updatedAt !== sow.createdAt && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Last Updated</label>
                      <p className="text-sm">{formatDate(sow.updatedAt.toString())}</p>
                    </div>
                  )}
                </div>

                {/* Property Information */}
                {sow.propertyData && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <h4 className="font-medium mb-3">Property Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {sow.propertyData.squareFootage && (
                        <div className="flex items-center gap-2">
                          <Home className="h-4 w-4 text-green-500" />
                          <div>
                            <label className="text-xs font-medium text-gray-600 block">Square Feet</label>
                            <p className="text-sm font-medium">{sow.propertyData.squareFootage.toLocaleString()}</p>
                          </div>
                        </div>
                      )}
                      
                      {sow.propertyData.bedrooms && (
                        <div className="flex items-center gap-2">
                          <Bed className="h-4 w-4 text-purple-500" />
                          <div>
                            <label className="text-xs font-medium text-gray-600 block">Bedrooms</label>
                            <p className="text-sm font-medium">{sow.propertyData.bedrooms}</p>
                          </div>
                        </div>
                      )}
                      
                      {sow.propertyData.bathrooms && (
                        <div className="flex items-center gap-2">
                          <Bath className="h-4 w-4 text-cyan-500" />
                          <div>
                            <label className="text-xs font-medium text-gray-600 block">Bathrooms</label>
                            <p className="text-sm font-medium">{sow.propertyData.bathrooms}</p>
                          </div>
                        </div>
                      )}
                      
                      {sow.propertyData.propertyValue && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-amber-500" />
                          <div>
                            <label className="text-xs font-medium text-gray-600 block">Property Value</label>
                            <p className="text-sm font-medium">${sow.propertyData.propertyValue.toLocaleString()}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Project Scope */}
                {sow.scopeDefinition && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <h4 className="font-medium mb-3">Project Scope</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-blue-500" />
                        <div>
                          <label className="text-xs font-medium text-gray-600 block">Project Type</label>
                          <p className="text-sm font-medium">{sow.scopeDefinition.projectType}</p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Finish Level</label>
                        <Badge variant="outline">{sow.scopeDefinition.finishLevel}</Badge>
                      </div>
                      
                      {sow.scopeDefinition.components.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-600 block mb-1">Components</label>
                          <div className="flex flex-wrap gap-1">
                            {sow.scopeDefinition.components.map((component, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {component}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Image Repository */}
          <ImageGallery 
            sowId={sowId!} 
            className="w-full"
          />

          {/* Customer Info */}
          {sow.customerInfo && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Name</label>
                    <p>{sow.customerInfo.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="text-sm">{sow.customerInfo.email}</p>
                  </div>
                  {sow.customerInfo.phone && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Phone</label>
                      <p className="text-sm">{sow.customerInfo.phone}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Project Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Project Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{sow.workItems.length}</div>
                  <div className="text-sm text-gray-600">Work Items</div>
                </div>
                
                {sow.questionnaireCompleted && (
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      <CheckCircle className="h-4 w-4" />
                      Questionnaire Complete
                    </div>
                  </div>
                )}
                
                <div className="text-center text-xs text-gray-500">
                  Created {new Date(sow.createdAt.toString()).toLocaleDateString()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Estimate */}
          {sow.totalEstimate && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Total Estimate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-lg font-semibold">
                    ${sow.totalEstimate.lineItems.reduce((sum, item) => 
                      sum + (item.laborCostLow + item.materials.reduce((matSum, mat) => 
                        matSum + (mat.lowCostPerUnit * mat.quantityLow), 0)), 0
                    ).toLocaleString()} - 
                    ${sow.totalEstimate.lineItems.reduce((sum, item) => 
                      sum + (item.laborCostHigh + item.materials.reduce((matSum, mat) => 
                        matSum + (mat.highCostPerUnit * mat.quantityHigh), 0)), 0
                    ).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Estimated project cost</div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};